import { Box, Tooltip } from '@mui/material';
import type { Dispatch, ReactElement, ReactNode, SetStateAction } from 'react';
import React, { useContext } from 'react';
import { autoCreateQuery, client } from '../../../organization/organization';
import OrganizationOptionRenderer from '../../../organization/OrganizationOptionRenderer';
import { getOption, quickAddOrganization } from '../../../organization/QuickAddOrganization';
import { personDirectoryFiltersReferenceAutocomplete } from '../../../person/person';
import { INACTIVE_FALSE_FILTER } from '../../constants/constants';
import { ENTITY_CONSTANTS } from '../../constants/entityConstants';
import { FORM_WIDGET_COMPONENTS, METADATA_NAMES } from '../../form/formConstants';
import { FormContainer } from '../../form/FormContainer';
import { FormContext } from '../../form/FormContext';
import FormItem from '../../form/FormItem';
import FormPanel from '../../form/FormPanel';
import FormWidget from '../../form/FormWidget';
import { useMutationWithContext } from '../../hooks/useMutationWithContext';
import I18n from '../../i18n/I18n';
import {
    type GetDefaultContactFieldsForPerson,
    useRepresentativeDefaultPrefill
} from './useRepresentativeDefaultPrefill';

type ContactFieldInputsProps = {
    /** Person reference ids to exclude from contact selection. */
    filterById: Array<string>;
    /** Stores quick-add person draft data when the user creates a new contact. */
    setPersonData: Dispatch<SetStateAction<Map<string, string>>>;
    /** Opens or closes the quick-add person editor. */
    setShowPersonEditor: Dispatch<SetStateAction<boolean>>;
    /** Show the organization selector with the contact and role fields. */
    showOrganization?: boolean;
    /** Resolves default role and organization values after a person is selected. */
    getDefaultContactFieldsForPerson?: GetDefaultContactFieldsForPerson;
    /** Disable contact, role, and organization fields. */
    disabled?: boolean;
    /** Tooltip shown while the contact fields are disabled. */
    disabledTooltip?: ReactNode;
};

const wrapWithDisabledTooltip = (field: ReactElement, disabled: boolean, tooltip?: ReactNode) => {
    if (!disabled || !tooltip) {
        return field;
    }

    return (
        <Tooltip title={tooltip} describeChild>
            <Box component="span" tabIndex={0} sx={{ display: 'block', width: '100%' }}>
                {field}
            </Box>
        </Tooltip>
    );
};

export default function ContactFieldInputs({
    filterById,
    setPersonData,
    setShowPersonEditor,
    showOrganization = false,
    getDefaultContactFieldsForPerson,
    disabled = false,
    disabledTooltip
}: ContactFieldInputsProps) {
    const { state, handleChanges } = useContext(FormContext);
    const [saveOrganization] = useMutationWithContext(autoCreateQuery, client);
    const selectedPersonId = state?.personReference?.id;

    useRepresentativeDefaultPrefill({
        currentOrganizationReference: state?.organizationReference,
        currentRole: state?.role,
        getDefaultContactFieldsForPerson,
        handleChanges,
        selectedPersonId,
        showOrganization
    });

    const onAddOption = (newValue) => {
        const nameParts = newValue.label?.trim().split(' ');
        const newFormData = new Map<string, string>([
            ['firstName', nameParts[0] || ''],
            ['lastName', nameParts.length > 1 ? nameParts[nameParts.length - 1] : '']
        ]);
        setPersonData(newFormData);
        setShowPersonEditor(true);
    };

    return (
        <FormPanel sx={{ padding: '0px 16px 8px 16px !important' }}>
            <FormContainer>
                <FormItem half>
                    {wrapWithDisabledTooltip(
                        <FormWidget
                            component={FORM_WIDGET_COMPONENTS.referenceAutocomplete}
                            entity={ENTITY_CONSTANTS.person}
                            name="personReference"
                            label={<I18n token="common.contact" />}
                            preload={false}
                            filter={personDirectoryFiltersReferenceAutocomplete}
                            filterById={filterById}
                            addOption
                            onCreateHandle={onAddOption}
                            disabled={disabled}
                        />,
                        disabled,
                        disabledTooltip
                    )}
                </FormItem>
                <FormItem half>
                    {wrapWithDisabledTooltip(
                        <FormWidget
                            component="MetadataAutocomplete"
                            metadataName={METADATA_NAMES.role}
                            identifier="termset"
                            allowCreate
                            name="role"
                            label={<I18n token="common.role" />}
                            disabled={disabled}
                        />,
                        disabled,
                        disabledTooltip
                    )}
                </FormItem>
                {showOrganization && (
                    <FormItem half>
                        {wrapWithDisabledTooltip(
                            <FormWidget
                                component={FORM_WIDGET_COMPONENTS.referenceAutocomplete}
                                entity={ENTITY_CONSTANTS.organization}
                                name="organizationReference"
                                label={<I18n token="common.organization" />}
                                preload={false}
                                OptionRenderer={OrganizationOptionRenderer}
                                getOption={getOption}
                                addOption
                                onCreateHandle={quickAddOrganization}
                                saveMutation={saveOrganization}
                                filter={INACTIVE_FALSE_FILTER}
                                disabled={disabled}
                            />,
                            disabled,
                            disabledTooltip
                        )}
                    </FormItem>
                )}
            </FormContainer>
        </FormPanel>
    );
}
